import React, { createContext, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Validation, ValidationManager, ValidationState } from '@vp/upload-and-print-validator';
import { useClientValidation } from 'src/clientValidation';
import { AnalyticsEventData, sendEventTracking } from 'src/components/Analytics';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useTrackingContext } from 'src/hooks/TrackingContextHooks';
import { usePerfectBound } from 'src/hooks/usePerfectBound';
import { UPLOAD_UNIFIED_EVENT, UploadSteps } from 'src/utils/Constants';
import { filterValidationToDisplay } from 'src/utils/filterValidationToDisplay';
import { addTemplateMismatchValidationIfNeeded } from 'src/utils/getTemplateMismatchValidation';
import { text } from 'src/utils/localizationUtil';
import { newRelicLog } from 'src/utils/newRelicUtil';
import { createTrackingDataForProduct } from 'src/utils/trackingUtil';
import { getValidationsForProduct, processValidations } from 'src/utils/validationUtil';
import { usePromptContext } from './PromptContextProvider';

export type PageDimensionValidationType = {
  page: number;
  actualWidth: number;
  actualHeight: number;
  expectedWidth: number;
  expectedHeight: number;
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
};

export interface ValidationContextProps {
  validations: Validation[] | null;
  validationPlaceholders: Validation[] | null;
  setValidations: (newValidations: Validation[] | null, showErrorModal?: boolean) => void;
  isValidating: boolean;
  setIsValidating: (isValidating: boolean) => void;
  validationManager: ValidationManager | null;
  fixedPageSizeWarning: (show: boolean | null) => void;
  showPageSizeWarningCallout: boolean | null;
}

export const ValidationContext = createContext<ValidationContextProps>({
  validations: null,
  validationPlaceholders: null,
  setValidations: () => {
    return;
  },
  isValidating: false,
  setIsValidating: () => {
    return;
  },
  validationManager: null,
  fixedPageSizeWarning: () => {
    return null;
  },
  showPageSizeWarningCallout: null,
});

export interface ValidationContextProviderProps {
  children?: React.ReactNode;
}

export const ValidationContextProvider = ({ children }: ValidationContextProviderProps): ReactElement => {
  const productContext = useProductContext();
  const { trackingConfiguration } = useTrackingContext();
  const { setShowValidationErrorPrompt } = usePromptContext();
  const { setClientValidation, isClientValidationEnabled } = useClientValidation();

  const [validationPlaceholders, setValidationPlaceholders] = useState<Validation[] | null>(null);
  const [validations, setValidationsInternal] = useState<Validation[] | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const trackingDataForProduct = createTrackingDataForProduct(productContext.productInformation);

  const [validationManager, setValidationManager] = useState<ValidationManager | null>(null);
  const { isPerfectBound } = usePerfectBound();

  const [showPageSizeWarningCallout, setShowPageSizeWarningCallout] = useState<boolean | null>(null);

  const fixedPageSizeWarning = useCallback((show: boolean | null) => {
    setShowPageSizeWarningCallout(show);
  }, []);

  const trackValidationEvents = useCallback(
    (processedValidations: Array<Validation>) => {
      processedValidations.forEach(validation => {
        if (validation.state === ValidationState.Error || validation.state === ValidationState.Warning) {
          sendEventTracking({
            ...trackingConfiguration,
            //Not spreading trackingDataForProduct here because that would cause this useCallback to have a dependency on trackingDataForProduct,
            //which would be a new object every update, causing this useCallback to be recalculated every update, even if the actual
            //properties did not change
            productId: trackingDataForProduct.product_id,
            name: trackingDataForProduct.name,
            core_product_id: trackingDataForProduct.core_product_id,
            core_product_version: trackingDataForProduct.core_product_version,
            action: UPLOAD_UNIFIED_EVENT,
            uploadStep:
              validation.state === ValidationState.Warning
                ? UploadSteps.ValidationWarning
                : UploadSteps.ValidationFailed,
            category: 'Design - Upload and Print',
            errorMessage: text(validation.message, validation.tokenValues),
            label: validation.id,
          } as AnalyticsEventData);
        }
      });
    },
    [
      trackingConfiguration,
      trackingDataForProduct.core_product_id,
      trackingDataForProduct.core_product_version,
      trackingDataForProduct.name,
      trackingDataForProduct.product_id,
    ]
  );

  /**
   * Error Modal will be set only when there are no more
   * validating states in the validation list
   */
  const showValidationErrorPrompt = useCallback(
    (processedValidations: Array<Validation>, showErrorModal: boolean) => {
      if (showErrorModal) {
        const validationContainsValidatingStates = processedValidations.some(
          validation => validation.state === ValidationState.Validating
        );
        if (!validationContainsValidatingStates) {
          const validationsWithErrorState = processedValidations.filter(
            validation => validation.state === ValidationState.Error
          );

          const validationsHaveErrorState = validationsWithErrorState.length > 0;
          setShowValidationErrorPrompt(validationsHaveErrorState);
        } else {
          setShowValidationErrorPrompt(false);
        }
      } else {
        setShowValidationErrorPrompt(false);
      }
    },
    [setShowValidationErrorPrompt]
  );

  const processAndUpdateValidations = useCallback(
    (validationList: Array<Validation>, showErrorModal: boolean) => {
      /**
       * Mutated validation list
       * to contain template mismatch validation
       * only for perfect bound booklets
       */
      const validationWithTemplateMismatchValidation = addTemplateMismatchValidationIfNeeded(
        validationList,
        isPerfectBound
      );
      const processedValidations = processValidations(
        validationWithTemplateMismatchValidation,
        productContext.productOptionTranslations
      );

      setValidationsInternal(processedValidations);
      trackValidationEvents(processedValidations);
      showValidationErrorPrompt(processedValidations, showErrorModal);
    },
    [isPerfectBound, productContext.productOptionTranslations, showValidationErrorPrompt, trackValidationEvents]
  );

  const resetValidationList = useCallback(() => {
    setValidationsInternal(null);
  }, []);

  const setValidations = useCallback(
    (newValidations: Validation[] | null, showErrorModal = true) => {
      newValidations ? processAndUpdateValidations(newValidations, showErrorModal) : resetValidationList();
    },
    [processAndUpdateValidations, resetValidationList]
  );

  /**
   * ValidationManager callback
   */
  const handleValidationListChange = useCallback(
    (validations: Validation[]) => {
      const validationToDisplay = filterValidationToDisplay(validations);
      setValidations(validationToDisplay);
    },
    [setValidations]
  );

  const createValidationManager = useCallback(() => {
    if (!validationManager) {
      const validationManager = new ValidationManager(
        productContext.completeProductOptions,
        productContext.designSpecification,
        handleValidationListChange
      );
      setValidationManager(validationManager);
    } else {
      newRelicLog('Client validation manager already exist');
    }
  }, [
    handleValidationListChange,
    productContext.completeProductOptions,
    productContext.designSpecification,
    validationManager,
  ]);

  useEffect(() => {
    if (
      isClientValidationEnabled &&
      Object.keys(productContext.completeProductOptions).length > 0 &&
      productContext.designSpecification.designViews.length > 0
    ) {
      try {
        createValidationManager();
      } catch (error) {
        setValidationManager(null);
        setClientValidation(false);
      }
    }
  }, [
    createValidationManager,
    isClientValidationEnabled,
    productContext.completeProductOptions,
    productContext.designSpecification.designViews.length,
    setClientValidation,
  ]);

  useEffect(() => {
    let isStale = false;

    async function getValidations() {
      if (productContext.isProductReady) {
        const requiredValidations = await getValidationsForProduct(productContext.productInformation);
        if (isStale) {
          return;
        }

        const processedValidations = processValidations(requiredValidations, productContext.productOptionTranslations);
        setValidationPlaceholders(processedValidations);
      }
    }

    getValidations();

    return () => {
      isStale = true;
    };
  }, [productContext]);

  const memoizedValue = useMemo(() => {
    return {
      validations: validations,
      validationPlaceholders: validationPlaceholders,
      setValidations,
      isValidating,
      setIsValidating,
      validationManager,
      showPageSizeWarningCallout,
      fixedPageSizeWarning,
    };
  }, [
    fixedPageSizeWarning,
    isValidating,
    setValidations,
    showPageSizeWarningCallout,
    validationManager,
    validationPlaceholders,
    validations,
  ]);

  return <ValidationContext.Provider value={memoizedValue}>{children}</ValidationContext.Provider>;
};
